import axios from "axios";
import { AGO_API_BASE_URL, API_BASE_URL, buildHeaders } from "utils/services";

export const fetchSpDataForDates = async (user, selectedDate, comparedDate) => {
  try {
    const [selectedData, comparedData] = await Promise.all([
      fetchStructuredProductsKpiDataForDate(user, selectedDate),
      fetchStructuredProductsKpiDataForDate(user, comparedDate),
    ]);

    return { selectedData, comparedData };
  } catch (error) {
    console.error("Error fetching KPI data for dates:", error);
    return {
      selectedData: getDefaultStructuredProductKpiData(),
      comparedData: getDefaultStructuredProductKpiData(),
    };
  }
};

export const fetchKpiDataForDates = async (user, type, selectedDate, comparedDate) => {
  let service;
  let default_datas;
  try {
    switch (type) {
      case 'sp':
        service = fetchStructuredProductsKpiDataForDate;
        default_datas = getDefaultStructuredProductKpiData;
        break;
      case 'margin':
        service = fetchMarginKpiDataForDate;
        default_datas = getDefaultMarginKpiData;
        break;
      case 'ago':
        service = fetchAgoKpiDataForDate;
        default_datas = getDefaultAgoKpiData;
        break;
      case 'copy_invest':
        service = fetchCopyInvestKpiDataForDate;
        default_datas = getDefaultCopyInvestKpiData;
        break;
      default:
        return;
    }
    const [selectedData, comparedData] = await Promise.all([
      service(user, selectedDate),
      service(user, comparedDate),
    ]);

    return { selectedData, comparedData };
  } catch (error) {
    console.error("Error fetching KPI data for dates:", error);
    return {
      selectedData: default_datas(),
      comparedData: default_datas(),
    };
  }
};

const fetchStructuredProductsKpiDataForDate = async (user, date) => {
  try {
    if (date.startDate === null || date.endDate === null) {
      return getDefaultStructuredProductKpiData();
    }

    const params = `?startDate=${date.startDate}&endDate=${date.endDate}`

    const [{ data: collectedAndProfit }, { data: investors }, { data: investorsMultiple }, { data: newClients }] = await Promise.all([
      axios.get(`${API_BASE_URL}/kpi/structured-products/collected-profit${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/kpi/structured-products/investors${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/kpi/structured-products/investors-multiple${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/kpi/structured-products/new-clients${params}`, buildHeaders(user)),
    ]);


    const kpiData = {
      volume: collectedAndProfit.collected || 0,
      profit: collectedAndProfit.profit || 0,
      investors: investors || 0,
      investors_multiple: investorsMultiple || 0,
      new_clients: newClients || 0
    };

    return kpiData;
  } catch (error) {
    console.error("Error fetching KPI data:", error);
    return getDefaultStructuredProductKpiData();
  }
};


const getDefaultStructuredProductKpiData = () => ({
  volume: 0,
  profit: 0,
  investors: 0,
  investors_multiple: 0,
  new_clients: 0
});

const getDefaultMarginKpiData = () => ({
  borrowed_amount_instant: 0,
  borrowed_amount: 0,
  borrowed_amount_fees: 0,
  gains: 0,
  open_positions: 0,
  avg_trade_per_client: 0,
  unique_clients: 0,
  clients_profit_loss: 0,
  volume: 0,
  volumeLong: 0,
  volumeShort: 0,
  trades_count: 0,
  new_clients: 0,
  bbook_algo: 0,
  bbook_all: 0
});

const fetchMarginKpiDataForDate = async (user, date) => {
  try {
    if (date.startDate === null || date.endDate === null) {
      return getDefaultMarginKpiData();
    }

    const params = `?startDate=${date.startDate}&endDate=${date.endDate}`

    const [
      { data: open_positions },
      { data: borrowed_amount_instant },
      { data: borrowed_amount },
      { data: borrowed_amount_fees },
      { data: trades },
      { data: clients_profit_loss },
      { data: gains },
      { data: volumes },
      { data: trades_count },
      { data: new_clients },
      { data: bbook_algo },
      { data: bbook_all },
    ] = await Promise.all([
      axios.get(`${API_BASE_URL}/leverage/kpi/open-positions/count${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/borrowed-amount/instant${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/borrowed-amount${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/borrowed-amount/fees${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/avg-trade-count${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/clients-profit-loss${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/gains${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/volume${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/trades/count${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/new-clients${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/bbook-algo${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/leverage/kpi/bbook-all${params}`, buildHeaders(user))
    ]);


    const kpiData = {
      open_positions,
      borrowed_amount_instant,
      borrowed_amount,
      borrowed_amount_fees,
      gains,
      avg_trade_per_client: trades.avg_trade,
      unique_clients: trades.unique_clients,
      clients_profit_loss,
      volume: volumes.volume,
      volumeLong: volumes.volumeLong,
      volumeShort: volumes.volumeShort,
      trades_count,
      new_clients,
      bbook_algo,
      bbook_all
    };

    return kpiData;
  } catch (error) {
    console.error("Error fetching KPI data:", error);
    return getDefaultStructuredProductKpiData();
  }
};

const getDefaultCopyInvestKpiData = () => ({
  active_followers: 0,
  new_follows: 0,
  unfollows: 0,
  followers_pnl: 0,
  total_invested: 0,
  cash_available: 0,
  volume: 0,
  profit: 0,
  goliaths_plus_profit: 0,
  recharge_sum: 0,
  recharge_avg: 0
});

const fetchCopyInvestKpiDataForDate = async (user, date) => {
  try {
    if (date.startDate === null || date.endDate === null) {
      return getDefaultMarginKpiData();
    }

    const params = `?startDate=${date.startDate}&endDate=${date.endDate}`

    const [
      { data: kpis },
      { data: followers },
      { data: total_invested },
      { data: volume },
      { data: goliaths_plus_profit },
      { data: recharge },

    ] = await Promise.all([
      axios.get(`${API_BASE_URL}/copy-invest/kpi${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/copy-invest/kpi/followers-timeframe${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/copy-invest/kpi/invested-timeframe${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/copy-invest/kpi/volume-timeframe${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/copy-invest/kpi/goliaths-plus-profit-timeframe${params}`, buildHeaders(user)),
      axios.get(`${API_BASE_URL}/copy-invest/kpi/recharge-timeframe${params}`, buildHeaders(user))
    ]);


    const kpiData = {
      active_followers: kpis.active_followers,
      new_follows: followers.new_follows,
      unfollows: followers.unfollows,
      followers_pnl: volume.followers_pnl,
      total_invested,
      cash_available: kpis.cash_available,
      volume: volume.volume,
      profit: volume.profit,
      goliaths_plus_profit,
      recharge_sum: recharge.recharge_sum,
      recharge_avg: recharge.recharge_avg
    };

    return kpiData;
  } catch (error) {
    console.error("Error fetching KPI data:", error);
    return getDefaultStructuredProductKpiData();
  }
};

const getDefaultAgoKpiData = () => ({
  ago_gains: 0,
  ago_avg_trade_per_client: 0,
  ago_unique_clients: 0,
  ago_clients_profit_loss: 0,
  ago_volumes: [],
  ago_trades_count: 0,
  deposits: 0,
  volume_total: 0
});

const fetchAgoKpiDataForDate = async (user, date) => {
  try {
    if (date.startDate === null || date.endDate === null) {
      return getDefaultAgoKpiData();
    }

    const params = `?startDate=${date.startDate}&endDate=${date.endDate}`
    const { data } = await axios.get(`${AGO_API_BASE_URL}/agbk/kpis${params}`, buildHeaders(user));

    const kpiData = {
      ago_gains: data?.data?.gains,
      ago_avg_trade_per_client: data?.data?.avg_trade_per_client,
      ago_unique_clients: data?.data?.unique_clients,
      ago_clients_profit_loss: data?.data?.clients_profit_loss,
      ago_volumes: data?.data?.volumes,
      ago_trades_count: data?.data?.trades_count,
      deposits: data?.data?.deposits,
      volume_total: data?.data?.volume_total
    };

    return kpiData;
  } catch (error) {
    console.error("Error fetching AGO KPI data:", error);
    return getDefaultAgoKpiData();
  }
};